import React, { useEffect, useState } from 'react'
import { ArrowRightOutlined } from '@ant-design/icons'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import ApprovePaymentModal from './ApprovePaymentModal'
import DebitFailureModal from './DebitFailureModal'
import theme from '../../../helpers/theme'
import CreditFailureDropdown from './CreditFailureDropdown'

const PaymentRequestsActions = ({ paymentRequest, updatePaymentRequests }) => {
  const [text, setText] = useState('')
  const [textColor, setTextColor] = useState(undefined)
  const [approveModalVisible, setApproveModalVisible] = useState(false)
  const [debitFailureModalVisible, setDebitFailureModalVisible] = useState(false)
  const [creditFailureDropdownVisible, setCreditFailureDropdownVisible] = useState(false)
  const [paymentRequestObject, setPaymentRequestObject] = useState(paymentRequest)
  const [transferFailureObject, setTransferFailureObject] = useState({})
  const [anchorEl, setAnchorEl] = useState(null)

  // Return codes where Funding Source is Removed
  const fundingSourceRemoved = [
    'R02',
    'R03',
    'R04',
    'R05',
    'R06',
    'R07',
    'R10',
    'R11',
    'R12',
    'R13',
    'R14',
    'R15',
    'R16',
    'R17',
    'R20',
    'R23',
    'R29',
    'R31',
    'R37',
    'R38',
    'R51',
  ]

  // Return codes where Funding Source is NOT Removed
  const notFundingSourceRemoved = ['R01', 'R08', 'R09', 'R22']

  const handleFailure = () => {
    // display error modals based on failure code

    // regex in case the failure object has a -late code
    const failureCodeMatch = transferFailureObject.code?.match(/^(R\d+)/)
    let failureCode = ''
    if (failureCodeMatch) {
      failureCode = failureCodeMatch[0]
    }
    if (notFundingSourceRemoved.includes(failureCode)) {
      // debit failure
      setDebitFailureModalVisible(true)
      return
    }
    if (fundingSourceRemoved.includes(failureCode)) {
      // credit failure
      setCreditFailureDropdownVisible(true)
      return
    }
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
    event.stopPropagation()
    if (!paymentRequest.dwolla_payment_id && text == 'Action Required') {
      // needs approval, open modal
      setApproveModalVisible(true)
      return
    }
    // transfer exists, check status. link not visible for 'pending' transfers
    const transferStatus = paymentRequestObject.transfer_object?.status
    switch (transferStatus) {
      case 'failed':
        handleFailure()
        break
    }
  }

  useEffect(() => {
    if (!paymentRequest) {
      return
    }
    setPaymentRequestObject(paymentRequest)
    if (paymentRequest.is_canceled) {
      setText('')
      return
    }
    if (paymentRequestObject.dwolla_payment_id) {
      const transferStatus = paymentRequestObject.transfer_object?.status
      switch (transferStatus) {
        case 'pending':
          setText('')
          break
        case 'failed':
          setTransferFailureObject(paymentRequestObject.transfer_failure_object)
          const failureCodeMatch = paymentRequest.transfer_failure_object?.code?.match(/^(R\d+)/)
          let failureCode = ''
          if (failureCodeMatch) {
            failureCode = failureCodeMatch[0]
          }
          if (notFundingSourceRemoved.includes(failureCode)) {
            // debit failure
            setText('Returned, see why')
            return
          }
          if (fundingSourceRemoved.includes(failureCode)) {
            // credit failure
            setText('Action Required. Payment Blocked')
            setTextColor(theme.palette.warning.main)
            return
          }
          break
        default:
          break
      }
      return
    }
    setText('Action Required')
  }, [paymentRequest, paymentRequestObject])

  return (
    <Stack direction='row' spacing={0.5}>
      {text && (
        <Link
          component='button'
          underline='none'
          onClick={(event) => handleClick(event)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '3px',
          }}
          color={textColor}
        >
          <ErrorOutlineOutlinedIcon />
          {text}
          <ArrowRightOutlined />
        </Link>
      )}

      {approveModalVisible && (
        <ApprovePaymentModal
          visible={approveModalVisible}
          handleCancelModal={() => {
            setApproveModalVisible(false)
          }}
          paymentRequest={paymentRequest}
          updatePaymentRequests={updatePaymentRequests}
        />
      )}
      {debitFailureModalVisible && (
        <DebitFailureModal
          visible={debitFailureModalVisible}
          handleCancelModal={() => setDebitFailureModalVisible(false)}
          paymentRequest={paymentRequest}
          failureObject={transferFailureObject}
          updatePaymentRequests={updatePaymentRequests}
        />
      )}
      {creditFailureDropdownVisible && (
        <CreditFailureDropdown
          paymentRequest={paymentRequest}
          failureObject={transferFailureObject}
          anchorElement={anchorEl}
          open={creditFailureDropdownVisible}
          onClose={() => setCreditFailureDropdownVisible(false)}
          updatePaymentRequests={updatePaymentRequests}
        />
      )}
    </Stack>
  )
}

export default PaymentRequestsActions
